import { v4 as uuidv4 } from 'uuid'
import { MESSAGE_TYPES, SESSION_TYPES } from '@/enums'

const initialState = () => ({
  botStatus: null,
  botConversationId: null,
  botEscalade: false,
  botEndTalk: false,
  botHandoff: false,
  botHandoffMessageSent: false,
  botHistory: null,
  botInProgress: false,
  botSessionsCount: 0,
  botOptions: {
    dyduSpace: null,
  },
  proactiveRules: [],
  botWelcomeMessageSent: false,
})

export const state = initialState()

export const getters = {
  dyduSpace: (state, getters, rootState) =>
    state.botOptions?.dyduSpace ||
    rootState.Widget.bot.botConfigAttributes.DYDU_BOT_SPACE ||
    '',
  displayWelcomeMessage: (state, getters, rootState, rootGetters) => {
    return (
      !rootGetters['Widget/isWidgetClosing'] &&
      !state.botWelcomeMessageSent &&
      rootState.labels.chatboxlabel?.bot_welcome_message &&
      !rootState.LiveChat.welcomeMessageSent
    )
  },
}

export const mutations = {
  RESET(state) {
    const newState = initialState()
    Object.keys(newState).forEach((key) => {
      state[key] = newState[key]
    })
  },
  UPDATE_BOT_STATUS(state, payload) {
    state.botStatus = payload
  },
  UPDATE_BOT_CONVERSATION_ID(state, payload) {
    state.botConversationId = payload
  },
  SET_BOT_ESCALADE(state) {
    state.botEscalade = true
  },
  RESET_BOT_ESCALADE(state) {
    state.botEscalade = false
  },
  SET_BOT_END_TALK(state) {
    state.botEndTalk = true
  },
  RESET_BOT_END_TALK(state) {
    state.botEndTalk = false
  },
  SET_BOT_IN_PROGRESS(state) {
    state.botInProgress = true
  },
  RESET_BOT_IN_PROGRESS(state) {
    state.botInProgress = false
  },
  SET_BOT_HANDOFF(state) {
    state.botHandoff = true
  },
  RESET_BOT_HANDOFF(state) {
    state.botHandoff = false
  },
  UPDATE_BOT_HANDOFF_MESSAGE_SENT(state, status) {
    state.botHandoffMessageSent = status
  },
  UPDATE_BOT_WELCOME_MESSAGE_SENT(state, status) {
    state.botWelcomeMessageSent = status
  },
  UPDATE_BOT_OPTIONS(state, { key, value }) {
    state.botOptions[key] = value
  },
  ADD_PROACTIVE_RULE(state, payload) {
    state.proactiveRules.push(payload)
  },
  SET_BOT_HISTORY(state, payload = null) {
    state.botHistory = payload
  },
  INCREMENT_BOT_SESSIONS_COUNT(state) {
    state.botSessionsCount++
  },
}

export const actions = {
  /**
   * Reset state
   * @param {*} { commit }
   */
  reset({ commit }) {
    commit('RESET')
  },
  /**
   * Return object well formated with bot specific keys
   *
   * @param {*} {}
   * @param {String} [message.content]
   * @param {Date} [message.timestamp]
   * @param {Object} [message.attributes]
   * @returns {Object}
   */
  botMessageFormated(_, message) {
    return {
      body: message.content,
      timestamp: message.timestamp || new Date(),
      sid: uuidv4(),
      type: message.type,
      attributes: {
        type: MESSAGE_TYPES.BOT,
        ...message.attributes,
      },
    }
  },
  /**
   * Push new bot message to the conversation
   * Fired when :
   * - client submit a message
   * - Bot send an answer
   * - Bot handoff message
   * @param {Object<Message>} payload
   */
  async updatesBotMessages({ commit, dispatch, rootState }, payload) {
    if (!rootState.Messages.scrollAlways) {
      commit('Messages/UPDATE_SCROLL_ALWAYS', true, { root: true })
    }

    const data = Array.isArray(payload) ? payload : [payload]
    const newMessages = await Promise.all(
      data.map((message) => dispatch('botMessageFormated', message)),
    )
    const lastMessage = newMessages[newMessages.length - 1]
    commit('Messages/UPDATE_LAST_MESSAGE_TIMESTAMP', lastMessage.timestamp, {
      root: true,
    })

    if (lastMessage.type === MESSAGE_TYPES.FEEDBACK) {
      dispatch('Messages/resetFeedback', lastMessage.sid, {
        root: true,
      })
    }

    commit('TimeSync/UPDATE_TIME_SYNC', null, { root: true })
    commit('Messages/UPDATE_MESSAGES_BOT', newMessages, { root: true })

    if (
      !lastMessage?.attributes?.incoming &&
      lastMessage?.attributes?.sidebar &&
      lastMessage.type !== MESSAGE_TYPES.FEEDBACK
    ) {
      commit('Sidebar/UPDATE_SIDEBAR_ID', lastMessage.sid, { root: true })
    }
  },
  /**
   * Set new message as bot home panel
   * Message with only sidebar, no content
   * @param {Object<Message>} payload
   */
  async updatesBotHome({ commit, dispatch, rootState }, payload) {
    if (rootState.Messages.scrollAlways) {
      commit('Messages/UPDATE_SCROLL_ALWAYS', false, { root: true })
    }
    const data = Array.isArray(payload) ? payload : [payload]
    const newMessages = await Promise.all(
      data.map((message) => dispatch('botMessageFormated', message)),
    )
    const lastMessage = newMessages[newMessages.length - 1]

    commit('Messages/UPDATE_LAST_MESSAGE_TIMESTAMP', lastMessage.timestamp, {
      root: true,
    })
    commit('TimeSync/UPDATE_TIME_SYNC', null, { root: true })
    commit('Messages/UPDATE_MESSAGES_BOT', newMessages, { root: true })
    commit('Messages/UPDATE_HOME_SID', lastMessage?.sid, { root: true })

    if (lastMessage?.attributes?.sidebar) {
      dispatch('Sidebar/toggleSidebar', lastMessage.sid, { root: true })
    }
  },
  /**
   * Set new message at the top of the chatbox
   * Fired when top knowledge is called
   * @param {Object<Message>} payload
   */
  async updatesBotFixedMessage({ commit, dispatch }, payload) {
    const data = Array.isArray(payload) ? payload : [payload]
    const newMessages = await Promise.all(
      data.map((message) => dispatch('botMessageFormated', message)),
    )

    commit('Messages/SET_FIXED_MESSAGES', newMessages, { root: true })

    const lastMessage = newMessages[newMessages.length - 1]

    if (
      lastMessage.type !== MESSAGE_TYPES.FEEDBACK &&
      lastMessage?.attributes?.sidebar
    ) {
      commit('Sidebar/UPDATE_SIDEBAR_ID', lastMessage.sid, {
        root: true,
      })
    }
  },
  initBotHandoff({ commit, dispatch }) {
    commit('UPDATE_BOT_HANDOFF_MESSAGE_SENT', false)
    commit('SET_BOT_HANDOFF')
    commit('INCREMENT_BOT_SESSIONS_COUNT')
    commit('History/SET_HISTORY_ITEM', null, { root: true })
    dispatch('Widget/updateSessionType', SESSION_TYPES.BOT, { root: true })
    commit('Widget/RESET_CLOSE_WIDGET', null, { root: true })
    commit('Widget/UPDATE_WIDGET_WARNING_TIMESTAMP', null, { root: true })
  },
  cleanBotConversation({ commit }) {
    commit('RESET_BOT_IN_PROGRESS')
    commit('UPDATE_BOT_CONVERSATION_ID', null)
    commit('RESET_BOT_HANDOFF')
    commit('Messages/REMOVE_FIXED_MESSAGES', null, { root: true })
  },
  resetBotWelcomeMessage({ commit }) {
    commit('UPDATE_BOT_WELCOME_MESSAGE_SENT', false)
  },
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
}
